import React, { useEffect, useState } from "react";
import {
  Layout,
  Typography,
  Table,
  Row,
  Col,
  Input,
  DatePicker,
  message,
  Button,
  Form,
  Modal,
  Select,
  Flex,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import dayjs, { Dayjs } from "dayjs";
import "../styles/App.css";
import {
  fetchHistory,
  updateHistory,
} from "src/redux/actions/VehicleHistoryAction";
import CustomTableLoader from "src/components/common/CustomTableLoader";
import {
  DownloadOutlined,
  EditOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { resetUpdateState } from "src/redux/slices/VehicleHistorySlice";
import { CSVLink } from "react-csv";
const { Content } = Layout;
const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

const History: React.FC = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const dataSource = useSelector((state: any) => state.history.data ?? []);
  const { totalPaid, totalBal } = useSelector((state: any) => state.history);
  const loading = useSelector((state: RootState) => state.history.loading);
  const update = useSelector((state: any) => state.history.update);
  const [fromDate, setFromDate] = useState<Dayjs | null>(
    dayjs().subtract(1, "day")
  );
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<null>(null);
  const [selectedCabId, setSelectedCabId] = useState<string | null>(null);
  const [originalAmount, setOriginalAmount] = useState<number>(0);
  const [originalBalance, setOriginalBalance] = useState<number>(0);

  const [searchText, setSearchText] = useState<string>("");
  const [cabIdFilter, setCabIdFilter] = useState<string>("");
  const [limit, setLimit] = useState<number>(10);
  const [skip, setSkip] = useState<number>(0);

  const getDefaultTuesday = () => {
    const today = dayjs();
    const dayOfWeek = today.day();
    if (dayOfWeek >= 2) {
      return today.startOf("week").add(2, "day");
    } else {
      return today.startOf("week").subtract(5, "day");
    }
  };

  const defaultTuesday = getDefaultTuesday();
  const defaultMonday = defaultTuesday.add(6, "day");
  useEffect(() => {
    setFromDate(defaultTuesday);
    setToDate(defaultMonday);

    const toDatePlusOne = defaultMonday.add(1, "day");

    dispatch(
      fetchHistory(
        defaultTuesday.format("YYYY-MM-DD"),
        toDatePlusOne.format("YYYY-MM-DD"),
        cabIdFilter.trim() || undefined
        // limit,
        // skip
      )
    );
  }, []);

  const handleFetch = () => {
    if (fromDate && toDate) {
      const from = fromDate.format("YYYY-MM-DD");
      const toPlusOne = dayjs(toDate).add(1, "day").format("YYYY-MM-DD");
      const filter = cabIdFilter.trim() !== "" ? cabIdFilter.trim() : undefined;

      dispatch(fetchHistory(from, toPlusOne, filter));
    } else {
      message.warning("Please select both From and To dates.");
    }
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
  };

  const filteredData = dataSource.filter((item: any) => {
    const matchesSearch = Object.values(item).some((field) =>
      String(field).toLowerCase().includes(searchText.toLowerCase())
    );
    const itemDate = item.date ? dayjs(item.date) : null;
    const withinDateRange =
      (!fromDate ||
        (itemDate && itemDate.isAfter(fromDate.subtract(1, "day")))) &&
      (!toDate || (itemDate && itemDate.isBefore(toDate.add(1, "day"))));
    return matchesSearch && (itemDate ? withinDateRange : true);
  });

  const columns: any = [
    {
      title: "Id",
      dataIndex: "Id",
      key: "Id",
      align: "center",
      // width: 0,
    },
    {
      title: "Cab ID",
      dataIndex: "CabId",
      key: "CabId",
      align: "center",
    },
    {
      title: "Date",
      dataIndex: "Date",
      key: "Date",
      align: "center",
      render: (text: any) => text.split(",")[0],
    },
    {
      title: "Time",
      dataIndex: "Date",
      key: "Time",
      align: "center",
      width: 90,
      render: (text: any) => {
        const timePart = text.split(",")[1]?.trim();
        if (!timePart) return "";
        const [time, ampm] = timePart.split(" ");
        const [hours, minutes] = time.split(":");
        return `${hours}:${minutes} ${ampm?.toUpperCase()}`;
      },
    },
    {
      title: "AmntRecDate",
      dataIndex: "amntRecDate",
      key: "Date",
      align: "center",
      render: (text: any) => text.split(",")[0],
    },
    {
      title: "Amount",
      dataIndex: "PaidAmount",
      key: "PaidAmount",
      align: "center",
    },
    {
      title: "Balance",
      dataIndex: "Balance",
      key: "Balance",
      align: "center",
    },
    {
      title: "BonusPoints",
      dataIndex: "BonusPoints",
      key: "BonusPoints",
      align: "center",
      width: 80,
    },
    {
      title: "Category Type",
      dataIndex: "category",
      key: "Category",
      align: "center",
    },
    {
      title: "Payment  Status",
      dataIndex: "PaymentDelay",
      key: "PaymentDelay",
      align: "center",
    },
    {
      title: "Mode of Payment",
      dataIndex: "paymentMode",
      key: "paymentMode",
      align: "center",
    },
    {
      title: "Reference No",
      dataIndex: "ReferNo",
      key: "ReferNo",
    },
    {
      title: "Remark",
      dataIndex: "remark",
      key: "remark",
    },
    {
      title: "Updated By",
      dataIndex: "UpdatedBy",
      key: "UpdatedBy",
    },
    {
      title: "More",
      key: "moreInfo",
      align: "center",
      width: 70,

      render: (_text: any, record: any) => (
        <Button
          icon={<EditOutlined />}
          onClick={() => handleOpenEditModal(record)}
        ></Button>
      ),
    },
  ];

  const handleOpenEditModal = (record: any) => {
    setEditingRecord(record);
    setSelectedCabId(record.CabId);

    setOriginalAmount(Number(record.Amount));
    setOriginalBalance(Number(record.Balance));

    form.setFieldsValue({
      Id: record.Id,
      CabId: record.CabId,
      PaidAmount: record.PaidAmount,
      Balance: record.Balance,
      PaymentDelay: record.PaymentDelay,
      ReferenceNo: record.ReferNo,
      Category: record.BonusPoints,
    });

    setIsEditModalVisible(true);
  };

  const handleEditSubmit = (values: any) => {
    const payload = {
      VId: values.Id,
      cabId: selectedCabId,
      paidAmount: String(values.PaidAmount),
      // balance: String(values.Balance),
      // bonusPoint: Number(values.Category),
      // paymentDelay: values.PaymentDelay,
      referNo: values.ReferenceNo,
      editedBy: userData.employeename,
    };
    dispatch(updateHistory(payload));
    setIsEditModalVisible(false);
    setEditingRecord(null);
    form.resetFields();
  };
  useEffect(() => {
    if (update?.success) {
      const toPlusOne = dayjs(toDate).add(1, "day").format("YYYY-MM-DD");
      dispatch(
        fetchHistory(fromDate ? fromDate.format("YYYY-MM-DD") : "", toPlusOne)
      );
      dispatch(resetUpdateState());
    }
  }, [update.success]);

  const disableFutureDates = (current: any) => {
    return current && current > dayjs().endOf("day");
  };

  const disableToDate = (current: any) => {
    if (!fromDate) return disableFutureDates(current);
    return (
      (current && current < fromDate.startOf("day")) ||
      (current && current > dayjs().endOf("day"))
    );
  };

  return (
    <Layout style={{ height: "calc(100vh - 100px)" }}>
      <Flex style={{ flexDirection: "column", rowGap: "20px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Title
            level={3}
            style={{
              fontSize: "35px",
              fontWeight: "bolder",
              color: "black",
              fontFamily: "serif",
              textShadow: "2px 2px 5px rgba(0, 0, 0, 0.3)",
              margin: 0,
            }}
          >
            Payment Report
          </Title>
        </div>
        <Row>
          <Col style={{ width: "40%", display: "flex", gap: "2%" }}>
            {/* <DatePicker
              value={fromDate}
              format="DD/MM/YYYY"
              onChange={(date) => setFromDate(date)}
              style={{ border: "1px solid grey" }}
              placeholder="From Date"
            />
            <DatePicker
              value={toDate}
              format="DD/MM/YYYY"
              onChange={(date) => setToDate(date)}
              style={{ border: "1px solid grey" }}
              placeholder="To Date"
            /> */}
            <DatePicker
              value={fromDate}
              format="DD/MM/YYYY"
              onChange={(date) => {
                setFromDate(date);
                if (toDate && date && toDate.isBefore(date, "day")) {
                  setToDate(null);
                }
              }}
              disabledDate={disableFutureDates}
              style={{ border: "1px solid grey" }}
              placeholder="From Date"
            />

            <DatePicker
              value={toDate}
              format="DD/MM/YYYY"
              onChange={(date) => setToDate(date)}
              disabledDate={disableToDate}
              style={{ border: "1px solid grey" }}
              placeholder="To Date"
            />
            {/* <Input
              placeholder="Filter by Cab ID"
              value={cabIdFilter}
              onChange={(e) => setCabIdFilter(e.target.value)}
              style={{
                border: "1px solid grey",
                width: "20%",
              }}
            /> */}
            <Button
              style={{ color: "white", background: "#800080", fontWeight: 500 }}
              onClick={handleFetch}
            >
              Go
            </Button>
          </Col>
          <Col
            style={{ width: "30%", gap: "2%", justifyContent: "end", display:'flex' }}
          >
            {/* <Typography.Text strong style={{ fontSize: "20px" }}>
              Balance : {totalBal || 0}
            </Typography.Text> */}
            <Typography.Text strong style={{ fontSize: "20px" }}>
              Paid : {totalPaid || 0}
            </Typography.Text>
          </Col>
          <Col
            style={{
              width: "30%",
              display: "flex",
              gap: "2%",
              justifyContent: "end",
            }}
          >
            <CSVLink data={filteredData} filename="Vehicle_History.csv">
              <Button
                icon={<DownloadOutlined />}
                style={{
                  background: "linear-gradient(45deg, #159957, #155799)",
                  color: "white",
                }}
              >
                Download
              </Button>
            </CSVLink>
            <Search
              id="historySearch"
              placeholder="Search"
              allowClear
              enterButton
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              onSearch={handleSearch}
              style={{ width: "180px" }}
            />
          </Col>
        </Row>
        <Table
          rowClassName={(record, index) =>
            index % 2 === 0 ? "table-row-light" : "table-row-dark"
          }
          loading={{
            spinning: loading,
            indicator: <CustomTableLoader />,
          }}
          dataSource={filteredData.map((item: any, index: any) => ({
            ...item,
            key: index,
          }))}
          columns={columns}
          bordered
          pagination={{
            // pageSize: limit,
            // current: skip / limit + 1,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            defaultPageSize: 100,
            // onChange: (page: number, pageSize: number) => {
            //   setLimit(pageSize);
            //   setSkip((page - 1) * pageSize);
            // },
            // onShowSizeChange: (current: number, size: number) => {
            //   setLimit(size);
            //   setSkip(0);
            // },
          }}
          locale={{ emptyText: "No history yet." }}
          scroll={{ x: "calc(500px + 70%)", y: 50 * 15 }}
        />
      </Flex>
      <Modal
        title={
          <div
            style={{
              backgroundColor: "purple",
              color: "white",
              textAlign: "center",
              padding: "10px 0",
              borderRadius: "4px",
              fontSize: "15px",
              fontWeight: "bold",
              marginTop: "20px",
            }}
          >
            {`Edit Details for ${selectedCabId}`}
          </div>
        }
        open={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        footer={null}
        modalRender={(modalNode) => <div>{modalNode}</div>}
      >
        <br />
        <Form
          form={form}
          layout="horizontal"
          labelCol={{ span: 10 }}
          wrapperCol={{ span: 14 }}
          onFinish={handleEditSubmit}
          onValuesChange={(changedValues, allValues) => {
            if ("Amount" in changedValues) {
              const newAmount = parseFloat(changedValues.Amount || 0);
              const updatedBalance =
                originalBalance - (originalAmount - newAmount);

              form.setFieldsValue({
                Balance: updatedBalance,
              });
            }
          }}
        >
          <Row gutter={16}>
            <Col span={20}>
              <Form.Item name="Id" label="ID">
                <Input disabled />
              </Form.Item>
             
              {/* <Form.Item name="Balance" label="Balance">
                <Input disabled />
              </Form.Item>
              <Form.Item
                name="PaymentDelay"
                label="Payment Status"
                rules={[
                  {
                    required: true,
                    message: "Please select payment delay option!",
                  },
                ]}
              >
                <Select placeholder="Select payment delay" disabled>
                  <Option value="1">On Time</Option>
                  <Option value="2">Delay</Option>
                </Select>
              </Form.Item>
              <Form.Item
                name="Category"
                label="Category"
                rules={[
                  {
                    required: true,
                    message: "Please select Category option!",
                  },
                ]}
              >
                <Select placeholder="Category" disabled>
                  {[12, 13, 14, 15, 16, 17, 18].map((val) => (
                    <Option key={val} value={val.toString()}>
                      {val}%
                    </Option>
                  ))}
                </Select>
              </Form.Item> */}
              <Form.Item name="CabId" label="CabId">
                <Input disabled />
              </Form.Item>
              <Form.Item name="ReferenceNo" label="Reference No	">
                <Input />
              </Form.Item>
               <Form.Item name="PaidAmount" label="Amount">
                <Input type="number" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item wrapperCol={{ span: 24 }}>
            <div style={{ textAlign: "center", marginTop: "16px" }}>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SendOutlined />}
                style={{ backgroundColor: "green" }}
              >
                Save
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default History;
